<template>
  <div data-testid="registration-page" style="position: relative">
    <!-- Loading Overlay -->
    <v-overlay v-model="activeLoading" class="align-center justify-center" :persistent="true" contained>
      <v-progress-circular color="primary" indeterminate size="48" width="3" />
    </v-overlay>
    <InfoBanner :show="showBanner" @close="showBanner = false" />
    <ExistingAccountMatchModal
      v-model="showAccountMatch"
      :matches="accountMatches"
      :lastName="formModel.lastName"
      :birthDate="formModel.birthDate"
      :asuRite="asuriteForMatch"
      @skip="dismissAccountMatch"
      @linked="goToLinkedAccount"
    />
    <div v-if="shouldDisplayFields" class="ea-container registration">
      <div class="ea-grid">
        <div class="registration__column ea-col-md-8 ea-start-md-3 ea-col-lg-6 ea-start-lg-4">
          <button
            v-if="!checkUserAuth"
            type="button"
            class="registration__back"
            @click="backButton"
            data-testid="registration-back-btn"
          >
            <img :src="chevronIcon" alt="" class="registration__back-icon" width="24" height="24" />
            Back
          </button>

          <h1 class="registration__title" data-testid="registration-title">Registration</h1>

          <v-form ref="form" v-model="valid" v-if="!error.message" class="ea-grid registration__form">
            <h2 class="registration__section-title">Personal information</h2>

            <!-- First / M.I. / Last: the M.I. column is fixed so its label fits on one line -->
            <div class="registration__names">
              <FormField
                id="fName"
                label="First name"
                required
                hint="Name must match photo ID"
                :error="firstNameErrorMsg"
                v-slot="{ id, isInvalid, ariaDescribedby }"
              >
                <v-text-field
                  :id="id"
                  v-model="formModel.firstName"
                  @keyup="firstNameValidation()"
                  @blur="
                    firstNameTouched = true;
                    firstNameValidation();
                  "
                  @keydown="changeTextToSentenseCase(formModel)"
                  required
                  placeholder="Enter first name"
                  variant="outlined"
                  hide-details
                  :error="isInvalid"
                  :aria-describedby="ariaDescribedby"
                  :disabled="firstNameExist"
                  data-testid="first-name-input"
                />
              </FormField>

              <FormField class="registration__middle-initial" id="mInitial" label="Middle initial" v-slot="{ id }">
                <v-text-field
                  :id="id"
                  aria-label="Middle initial"
                  v-model="formModel.middleName"
                  maxlength="1"
                  placeholder="M.I."
                  variant="outlined"
                  hide-details
                  data-testid="middle-initial-input"
                />
              </FormField>

              <FormField
                id="lName"
                label="Last name"
                required
                :error="lastNameErrorMsg"
                v-slot="{ id, isInvalid, ariaDescribedby }"
              >
                <v-text-field
                  :id="id"
                  @keyup="lastNameValidation()"
                  @blur="
                    lastNameTouched = true;
                    lastNameValidation();
                    offerExistingAccountMatch();
                  "
                  @keydown="changeTextToSentenseCase(formModel)"
                  v-model="formModel.lastName"
                  placeholder="Enter last name"
                  variant="outlined"
                  hide-details
                  :error="isInvalid"
                  :aria-describedby="ariaDescribedby"
                  :disabled="lastNameExist"
                />
              </FormField>
            </div>

            <FormField
              class="ea-col-sm-6"
              id="E-mail"
              label="Email address"
              required
              hint="Where we will send all important emails"
              :error="emailErrorMsg"
              v-slot="{ id, isInvalid, ariaDescribedby }"
            >
              <v-text-field
                :id="id"
                type="email"
                @change="emailValidation()"
                @blur="
                  emailTouched = true;
                  emailValidation();
                "
                v-model="formModel.emailId"
                required
                placeholder="Enter email address"
                variant="outlined"
                hide-details
                :error="isInvalid"
                :aria-describedby="ariaDescribedby"
                :disabled="emailExist || emailVerified || emailFromQuery"
              />
            </FormField>

            <FormField
              class="ea-col-sm-6"
              id="birthdate"
              label="Birthday"
              required
              hint="Required for verification"
              :error="dobTouched ? dobErrorMsg : ''"
              v-slot="{ id, isInvalid, ariaDescribedby }"
            >
              <v-text-field
                :id="id"
                type="date"
                v-model="formModel.birthDate"
                @change="handleDOBChange()"
                @input="handleDOBChange()"
                @blur="dobTouched = true"
                class="registration__date"
                required
                variant="outlined"
                hide-details
                :error="isInvalid"
                :aria-describedby="ariaDescribedby"
                :disabled="dobExist"
                :max="maxBirthDate"
                min="1900-01-01"
              />
            </FormField>

            <div v-if="contact && contact.age && contact.age <= 21">
              <span id="diploma-question-label" class="registration__label">
                Do you have a high school diploma or GED equivalent?<span
                  class="registration__required"
                  aria-hidden="true"
                  >*</span
                >
              </span>
              <div class="registration__radios" role="radiogroup" aria-labelledby="diploma-question-label">
                <label class="registration__radio">
                  <input type="radio" name="isDiplomaOrGed" value="Yes" required v-model="formModel.isDiplomaOrGed" />
                  <span>Yes</span>
                </label>
                <label class="registration__radio">
                  <input type="radio" name="isDiplomaOrGed" value="No" required v-model="formModel.isDiplomaOrGed" />
                  <span>No</span>
                </label>
              </div>
            </div>

            <!-- ULC-3111: only when the learner answers "No" to the diploma question -->
            <template v-if="showHighSchoolFields">
              <div data-testid="hs-graduation-fields">
                <span id="hs-graduation-question" class="registration__label">
                  What is your expected high school graduation date?
                </span>
                <div class="ea-grid" role="group" aria-labelledby="hs-graduation-question">
                  <FormField
                    class="ea-col-sm-6"
                    label="Month"
                    :required="!formModel.highSchoolGraduationDeclined"
                    v-slot="{ id }"
                  >
                    <v-select
                      :id="id"
                      v-model="formModel.highSchoolGraduationMonth"
                      :items="monthOptions"
                      placeholder="Enter month"
                      menu-icon="mdi-chevron-down"
                      :menu-props="shortMenuProps"
                      :list-props="{ density: 'compact' }"
                      variant="outlined"
                      hide-details
                      :disabled="formModel.highSchoolGraduationDeclined"
                      data-testid="hs-graduation-month"
                    />
                  </FormField>
                  <FormField
                    class="ea-col-sm-6"
                    label="Year"
                    :required="!formModel.highSchoolGraduationDeclined"
                    v-slot="{ id }"
                  >
                    <v-select
                      :id="id"
                      v-model="formModel.highSchoolGraduationYear"
                      :items="graduationYearOptions"
                      placeholder="Enter year"
                      menu-icon="mdi-chevron-down"
                      :menu-props="shortMenuProps"
                      :list-props="{ density: 'compact' }"
                      variant="outlined"
                      hide-details
                      :disabled="formModel.highSchoolGraduationDeclined"
                      data-testid="hs-graduation-year"
                    />
                  </FormField>
                </div>
                <label class="registration__checkbox mt-4" data-testid="hs-graduation-declined">
                  <input v-model="formModel.highSchoolGraduationDeclined" type="checkbox" />
                  <span>I prefer not to provide this information</span>
                </label>
              </div>

              <FormField class="ea-col-sm-6 registration__hs-school" label="School Type" v-slot="{ id }">
                <v-select
                  :id="id"
                  v-model="formModel.highSchoolType"
                  :items="highSchoolTypes"
                  placeholder="Select school type"
                  menu-icon="mdi-chevron-down"
                  :menu-props="shortMenuProps"
                  :list-props="{ density: 'compact' }"
                  variant="outlined"
                  hide-details
                  data-testid="hs-school-type"
                />
              </FormField>

              <FormField
                class="ea-col-sm-6 registration__hs-school"
                label="Name of current school of attendance"
                v-slot="{ id }"
              >
                <v-text-field
                  :id="id"
                  v-model="formModel.currentHighSchool"
                  placeholder="Enter name of current school"
                  maxlength="255"
                  variant="outlined"
                  hide-details
                  data-testid="hs-school-name"
                />
              </FormField>
            </template>

            <h2 class="registration__section-title">Contact information</h2>

            <FormField label="Address line 1" required :error="fields.address.error">
              <street-address-field
                ref="streetField"
                v-model:street="formModel.mailingAddress.street"
                v-model:place="formModel.place"
                :places-ready="placesReady"
                :places-failed="placesFailed"
                :error="!!fields.address.error"
                @manual-input="addressCheck"
              />
            </FormField>

            <FormField label="Address line 2" v-slot="{ id }">
              <v-text-field
                :id="id"
                v-model="formModel.mailingAddress.addressLine2"
                placeholder="Apt, suite, unit, building, floor, etc."
                variant="outlined"
                hide-details="auto"
                :rules="[englishAddressRule]"
              />
            </FormField>

            <FormField class="ea-col-sm-4" label="City" required v-slot="{ id }">
              <v-text-field
                :id="id"
                v-model="formModel.mailingAddress.city"
                @keydown="changeTextToSentenseCase(formModel)"
                placeholder="Enter city"
                variant="outlined"
                hide-details="auto"
                :rules="[(v) => !!v || 'This is required', englishAddressRule]"
              />
            </FormField>

            <FormField class="ea-col-sm-4" label="Country" required v-slot="{ id }">
              <v-autocomplete
                :id="id"
                v-model="selectedCountryCode"
                :items="countryOptions"
                item-value="value"
                item-title="title"
                placeholder="Enter country"
                menu-icon=""
                variant="outlined"
                hide-details="auto"
                :rules="[(v) => !!v || 'This is required']"
                @update:model-value="onCountryChange"
              />
            </FormField>

            <FormField class="ea-col-sm-4" label="State" :required="!!stateOptions.length" v-slot="{ id }">
              <v-autocomplete
                v-if="stateOptions.length"
                :id="id"
                v-model="selectedState"
                :items="stateOptions"
                item-value="value"
                item-title="title"
                placeholder="Enter state"
                menu-icon=""
                variant="outlined"
                hide-details="auto"
                :rules="[(v) => !!v || 'This is required']"
              />
              <v-text-field
                v-else
                :id="id"
                v-model="formModel.mailingAddress.state"
                placeholder="Enter state or region"
                variant="outlined"
                hide-details="auto"
                :rules="[englishAddressRule]"
              />
            </FormField>

            <FormField class="ea-col-sm-6 ea-col-md-4" label="Zip code" required v-slot="{ id }">
              <v-text-field
                :id="id"
                v-model="formModel.mailingAddress.zipCode"
                placeholder="Enter zip code"
                variant="outlined"
                hide-details="auto"
                :rules="[() => zipcodeCheck(), englishAddressRule]"
              />
            </FormField>

            <FormField
              class="ea-col-sm-6 ea-col-md-8"
              label="Phone number"
              required
              :error="fields.phoneNumber.error"
              v-slot="{ id, ariaDescribedby }"
            >
              <div class="registration__phone" :class="{ 'registration__phone--error': !!fields.phoneNumber.error }">
                <vue-tel-input
                  v-model="formModel.phoneNumber"
                  @validate="onInput"
                  @blur="
                    phoneNumberTouched = true;
                    validatePhoneNumber();
                  "
                  :preferredCountries="['us', 'gb']"
                  invalidMsg="Please enter a valid number"
                  :validCharactersOnly="true"
                  :autoDefaultCountry="false"
                  :defaultCountry="'us'"
                  :inputOptions="{ id, placeholder: 'Enter phone number', 'aria-describedby': ariaDescribedby }"
                >
                  <template #arrow-icon>
                    <img :src="dropdownIcon" alt="" width="16" height="16" />
                  </template>
                </vue-tel-input>
              </div>
            </FormField>

            <v-btn
              @click="signUp()"
              :style="{
                'pointer-events': isFormValid ? 'auto' : 'none',
                cursor: isFormValid ? 'pointer' : 'not-allowed'
              }"
              rounded
              class="save-and-confirm registration__submit"
              :class="{ 'registration__submit--invalid': !isFormValid }"
              :ripple="false"
              variant="flat"
            >
              Continue
            </v-btn>
          </v-form>
          <error-component :message="error.message" :cta="error.cta" v-else v-on:clear="clear()" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import ErrorComponent from './ErrorComponent.vue';
import InfoBanner from './InfoBannerComponent.vue';
import ExistingAccountMatchModal from './ExistingAccountMatchModal.vue';
import StreetAddressField from './StreetAddressField.vue';
import FormField from './ui/FormField.vue';
import { VueTelInput } from 'vue-tel-input';
import chevronIcon from '@/assets/icons/chevron-down-dark.svg';
import dropdownIcon from '@/assets/icons/chevron-down-gray.svg';
import { DataLayerMixin } from '../mixins/dataLayerMixin.js';
import { AjaxLoader } from '../mixins/ajaxLoader.js';
import { FetchPartnerData } from '../mixins/fetchPartnerDataMixin.js';
import { Helper } from '../mixins/helpers.js';
import { useUserStore } from '@/stores/user';
import { useRegistrationStore } from '@/stores/registration';
import { useCountryData } from '@/composables/useCountryData';
import { isFeatureEnabled } from '../../feature-flags';
import { importLibrary } from '@/utils/mapsLoader';
import { foldAccents, isEnglishAddressText, isEnglishName, ENGLISH_ONLY_MESSAGE } from '@/utils/englishText';
import { MONTH_OPTIONS, HIGH_SCHOOL_TYPES, SHORT_MENU_PROPS, graduationYearOptions } from '@/utils/hsGraduation';
import { MIN_LEARNER_AGE, ageInYears, latestBirthDateForAge } from '@/utils/dateUtils';

export default defineComponent({
  name: 'RegisterV2Component',

  props: {
    email: {
      type: String,
      default: ''
    }
  },

  setup() {
    const {
      countries,
      loading: countriesLoading,
      fetchCountryData,
      countryOptions,
      getStateOptions
    } = useCountryData();

    return {
      countries,
      countriesLoading,
      fetchCountryData,
      countryOptions,
      getStateOptions
    };
  },

  data() {
    return {
      chevronIcon,
      dropdownIcon,
      placesReady: false,
      placesFailed: false,
      shouldDisplayFields: false,
      showAccountMatch: false,
      accountMatches: [],
      // lastName|birthDate pairs the account-match lookup has run for / been waved off for
      accountMatchCheckedKey: '',
      accountMatchDismissedKeys: [],
      accountMatchInFlight: null,
      accountMatchTimer: null,
      accountMatchFromSubmit: false,
      selectedCountryCode: 'USA',
      stateOptions: [],
      contact: {},
      valid: true,
      hsDiplomaGEDOptions: ['Yes', 'No'],
      monthOptions: MONTH_OPTIONS,
      graduationYearOptions: graduationYearOptions(),
      highSchoolTypes: HIGH_SCHOOL_TYPES,
      shortMenuProps: SHORT_MENU_PROPS,
      dob: {
        birth_day: '',
        birth_month: '',
        birth_year: ''
      },
      years: [],
      days: '1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31'.split(' '),
      days28: '1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28'.split(' '),
      days29: '1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29'.split(' '),
      days30: '1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30'.split(' '),
      days31: '1 2 3 4 5 6 7 8 9 10 11 12 13 14 15 16 17 18 19 20 21 22 23 24 25 26 27 28 29 30 31'.split(' '),
      months: 'January February March April May June July August September October November December'.split(' '),
      error: {
        message: '',
        cta: ''
      },
      formModel: {
        emailId: '',
        firstName: '',
        middleName: '',
        lastName: '',
        place: '',
        mailingAddress: {
          country: 'United States',
          place: '',
          city: '',
          zipCode: '',
          street: '',
          state: '',
          addressLine2: ''
        },
        phoneNumber: '',
        isDiplomaOrGed: '',
        highSchoolGraduationMonth: null,
        highSchoolGraduationYear: null,
        highSchoolGraduationDeclined: false,
        highSchoolType: null,
        currentHighSchool: ''
      },
      emailErrorMsg: '',
      firstNameErrorMsg: '',
      lastNameErrorMsg: '',
      dobErrorMsg: '',
      dobTouched: false,
      firstNameTouched: false,
      lastNameTouched: false,
      emailTouched: false,
      phoneNumberTouched: false,
      phoneNumberValid: true, // Default to true to avoid initial validation issues
      emailVerified: false,
      showBanner: false, // ULC-3105: ASU email account message disabled
      contactFormModel: {},
      fields: {
        phoneNumber: {
          error: ''
        },
        address: {
          error: '',
          city: '',
          state: '',
          country: '',
          postalCode: ''
        }
      }
    };
  },

  mixins: [DataLayerMixin, FetchPartnerData, Helper, AjaxLoader],

  components: {
    ErrorComponent,
    ExistingAccountMatchModal,
    FormField,
    InfoBanner,
    StreetAddressField,
    VueTelInput
  },

  watch: {
    // One letter only; the API stores it as Contact.MiddleName.
    'formModel.middleName'(value) {
      const initial = (value || '')
        .replace(/[^A-Za-z]/g, '')
        .slice(0, 1)
        .toUpperCase();

      if (initial !== value) this.formModel.middleName = initial;
    }
  },

  computed: {
    welcomePageTitle() {
      const userStore = useUserStore();
      return (userStore.welcomePageData && userStore.welcomePageData.studentPortalTitle) || '';
    },
    firstNameExist() {
      return !!this.$route.params.firstName;
    },
    lastNameExist() {
      return !!this.$route.params.lastName;
    },
    emailExist() {
      return !!(this.email || this.$route.params.email || this.emailVerified);
    },
    emailFromQuery() {
      return !!(this.email || this.$route.query.email || this.emailVerified);
    },
    dobExist() {
      return !!this.$route.params.birthDate;
    },
    checkUserAuth() {
      return this.$auth.isAuthenticated();
    },
    // Only the Continue with ASURITE arrival can produce the duplicate this step exists to
    // prevent; email/OTP signups already resolve to their own account.
    asuriteForMatch() {
      if (!this.$auth.isAuthenticated() || this.$auth.getActiveProviderType() === 'custom_oauth') return '';

      return this.$auth.getUsername() || '';
    },
    // The ASURITE arrival lands here with its email prefilled (emailVerified); a hand-typed
    // email means a different entry point, so the check stays out of the way.
    accountMatchEligible() {
      return isFeatureEnabled('ASURITE_ACCOUNT_MATCH') && !!this.asuriteForMatch && this.emailVerified;
    },
    // Both inputs the lookup needs, or '' while either is missing or not yet a real date.
    accountMatchKey() {
      const lastName = (this.formModel.lastName || '').trim();
      const birthDate = this.formModel.birthDate || '';

      if (!lastName || !/^\d{4}-\d{2}-\d{2}$/.test(birthDate) || birthDate < '1900-01-01') return '';
      if (!(ageInYears(birthDate) >= MIN_LEARNER_AGE)) return '';

      return `${lastName.toLowerCase()}|${birthDate}`;
    },
    availableDays() {
      if (!this.dob.birth_month) {
        return this.days31; // Default to 31 days
      }

      if (this.dob.birth_month == 'February') {
        const isLeapYear = this.dob.birth_year && this.dob.birth_year % 4 == 0;
        return isLeapYear ? this.days29 : this.days28;
      } else if (
        ['January', 'March', 'May', 'July', 'August', 'October', 'December'].indexOf(this.dob.birth_month) >= 0
      ) {
        return this.days31;
      } else {
        return this.days30;
      }
    },
    signupFormData() {
      const formData = { ...this.formModel };

      // Only include isDiplomaOrGed if contact age is > 21
      if (this.contact && this.contact.age && this.contact.age > 21) {
        delete formData.isDiplomaOrGed;
      }

      // ULC-3111: month/year stay in the model so Back restores them; the API reads the
      // combined YYYY-MM. Dropped entirely unless the learner answered "No".
      if (this.showHighSchoolFields) {
        const { highSchoolGraduationMonth: month, highSchoolGraduationYear: year } = formData;
        const declined = Boolean(formData.highSchoolGraduationDeclined);
        formData.highSchoolGraduationDeclined = declined;
        formData.highSchoolGraduationDate = !declined && month && year ? `${year}-${month}` : '';
        formData.currentHighSchool = (formData.currentHighSchool || '').trim();
      } else {
        delete formData.highSchoolGraduationMonth;
        delete formData.highSchoolGraduationYear;
        delete formData.highSchoolGraduationDeclined;
        delete formData.highSchoolType;
        delete formData.currentHighSchool;
      }

      return formData;
    },
    showHighSchoolFields() {
      // Under 18 only (ticket + Figma); the diploma question itself still shows up to 21.
      return Boolean(this.contact?.age && this.contact.age < 18 && this.formModel.isDiplomaOrGed === 'No');
    },
    maxBirthDate() {
      return latestBirthDateForAge(MIN_LEARNER_AGE);
    },

    // '' reads as a selected item to v-autocomplete and hides its placeholder
    selectedState: {
      get() {
        return this.formModel.mailingAddress.state || null;
      },
      set(value) {
        this.formModel.mailingAddress.state = value || '';
      }
    },

    // Checks the editable fields, not Google's cached place label
    hasNonEnglishAddress() {
      const { street, city, state, zipCode, addressLine2 } = this.formModel.mailingAddress;

      return ![street, city, state, zipCode, addressLine2].every(isEnglishAddressText);
    },

    isFormValid() {
      // Check if all required fields are filled and have no errors
      const hasRequiredFields =
        this.formModel.firstName &&
        this.formModel.lastName &&
        this.formModel.emailId &&
        this.formModel.birthDate &&
        this.formModel.phoneNumber &&
        (this.formModel.mailingAddress.street || '').trim() &&
        this.formModel.mailingAddress.city &&
        (this.stateOptions.length === 0 || this.formModel.mailingAddress.state) &&
        this.formModel.mailingAddress.country &&
        this.formModel.mailingAddress.zipCode;

      const hasNoErrors =
        !this.firstNameErrorMsg &&
        !this.lastNameErrorMsg &&
        !this.emailErrorMsg &&
        !this.dobErrorMsg &&
        !this.fields.phoneNumber.error &&
        !this.fields.address.error &&
        !this.hasNonEnglishAddress;

      // Check diploma/GED if applicable
      const diplomaValid = !this.contact?.age || this.contact.age > 21 || this.formModel.isDiplomaOrGed;

      // ULC-3111: month and year are required once the high school fields show, unless declined
      const graduationDateValid =
        !this.showHighSchoolFields ||
        Boolean(this.formModel.highSchoolGraduationDeclined) ||
        Boolean(this.formModel.highSchoolGraduationMonth && this.formModel.highSchoolGraduationYear);

      return hasRequiredFields && hasNoErrors && diplomaValid && graduationDateValid;
    }
  },

  methods: {
    onCountryChange(countryCode) {
      this.stateOptions = this.getStateOptions(countryCode);
      const country = this.countries.find((c) => c.countryCode === countryCode);
      if (country) {
        this.formModel.mailingAddress.country = country.description;
      }
      // Clear state if it doesn't exist in new country
      if (this.formModel.mailingAddress.state) {
        const stateExists = this.stateOptions.some((s) => s.value === this.formModel.mailingAddress.state);
        if (!stateExists) {
          this.formModel.mailingAddress.state = '';
        }
      }
    },
    backButton() {
      this.$router.push({ name: 'WelcomeDesktop' });
    },

    emailValidation() {
      if (!this.emailTouched) return;

      if (!this.formModel.emailId) {
        this.emailErrorMsg = 'E-mail is required';
      } else if (
        !/.+@.+/.test(this.formModel.emailId) ||
        !/^[\w.-]+([-+]?[\w.-]+)*@\w+([-]?\w+)*(\.\w{2,})+$/.test(this.formModel.emailId)
      ) {
        this.emailErrorMsg = 'E-mail must be valid';
      } else {
        this.emailErrorMsg = '';
      }
    },

    lastNameValidation() {
      if (!this.lastNameTouched) return;

      if (!this.formModel.lastName) {
        this.lastNameErrorMsg = 'Last name is required';
      } else if (this.formModel.lastName.length >= 24) {
        this.lastNameErrorMsg = 'Name must be less than 24 characters';
      } else if (!isEnglishName(this.formModel.lastName)) {
        this.lastNameErrorMsg = ENGLISH_ONLY_MESSAGE;
      } else {
        this.lastNameErrorMsg = '';
      }
    },

    firstNameValidation() {
      if (!this.firstNameTouched) return;

      if (!this.formModel.firstName) {
        this.firstNameErrorMsg = 'First name is required';
      } else if (this.formModel.firstName.length >= 24) {
        this.firstNameErrorMsg = 'Name must be less than 24 characters';
      } else if (!isEnglishName(this.formModel.firstName)) {
        this.firstNameErrorMsg = ENGLISH_ONLY_MESSAGE;
      } else {
        this.firstNameErrorMsg = '';
      }
    },

    zipcodeCheck() {
      if (
        (this.formModel.mailingAddress.country == 'US' || this.formModel.mailingAddress.country == 'United States') &&
        !this.formModel.mailingAddress.zipCode
      ) {
        return 'This is required';
      } else {
        return true;
      }
    },

    englishAddressRule(value) {
      return isEnglishAddressText(value) || ENGLISH_ONLY_MESSAGE;
    },

    // Typing without picking a suggestion must still yield a street, not just place
    addressCheck() {
      if (!(this.formModel.mailingAddress.street || '').trim()) {
        this.fields.address.error = 'This field is required';
      } else if (!isEnglishAddressText(this.formModel.mailingAddress.street)) {
        this.fields.address.error = ENGLISH_ONLY_MESSAGE;
      } else {
        this.fields.address.error = '';
      }
    },

    onInput(payload) {
      // Store the validation result but don't show error until field is touched
      this.phoneNumberValid = payload?.isValid || payload?.valid;
      if (this.phoneNumberTouched) {
        this.validatePhoneNumber();
      }
    },

    validatePhoneNumber() {
      if (!this.phoneNumberTouched) return;

      if (!this.formModel.phoneNumber) {
        this.fields.phoneNumber.error = 'Phone number is required';
      } else if (this.phoneNumberValid === false) {
        this.fields.phoneNumber.error = 'Please enter a valid phone number';
      } else {
        this.fields.phoneNumber.error = '';
      }
    },

    updateDOB() {
      function padZero(digit) {
        digit = parseInt(digit);
        if (digit < 10) return `0${digit}`;
        return digit;
      }

      // Check if the currently selected day is valid for the new month/year combination
      if (this.dob.birth_month && this.dob.birth_day) {
        const maxValidDay = this.availableDays.length;
        if (parseInt(this.dob.birth_day) > maxValidDay) {
          this.dob.birth_day = '';
        }
      }

      // Create birthDate string if all fields are filled
      if (this.dob.birth_day && this.dob.birth_month && this.dob.birth_year) {
        this.formModel.birthDate =
          this.dob.birth_year +
          '-' +
          padZero(this.months.indexOf(this.dob.birth_month) + 1) +
          '-' +
          padZero(this.dob.birth_day);

        // Calculate age and update contact object
        const age = ageInYears(this.formModel.birthDate) || 0;
        this.contact = { ...this.contact, age: age };
        this.formModel.age = age;
      }
    },

    updateDOBFromDate() {
      if (this.formModel.birthDate) {
        // Parse the date and update the old dob object for compatibility
        const birthDate = new Date(this.formModel.birthDate);
        this.dob.birth_year = birthDate.getFullYear();
        this.dob.birth_month = this.months[birthDate.getMonth()];
        this.dob.birth_day = birthDate.getDate().toString();

        // Calculate age and update contact object
        const age = ageInYears(this.formModel.birthDate) || 0;
        this.contact = { ...this.contact, age: age };
        this.formModel.age = age;

        // Clear any error messages
        this.dobErrorMsg = '';
      }
    },

    handleDOBChange() {
      this.dobTouched = true;
      this.updateDOBFromDate();
      this.dobValidationRule();
      this.scheduleExistingAccountMatch();
    },

    dobValidationRule() {
      if (!this.formModel.birthDate) {
        this.dobErrorMsg = 'Birthday is required';
        this.dobTouched = true; // Mark as touched to show error
        return 'Birthday is required';
      }

      // Only show validation errors if field has been touched
      if (!this.dobTouched) {
        return true;
      }

      if (!(ageInYears(this.formModel.birthDate) >= MIN_LEARNER_AGE)) {
        this.dobErrorMsg = 'Age is too low';
        return 'Age is too low';
      }

      this.dobErrorMsg = '';
      return true;
    },

    clear() {
      this.error.message = '';
    },

    validateAge() {
      if (!this.dob.birth_year) return 'Year is required';

      if (!this.formModel.birthDate) return 'Please complete your birth date';

      this.formModel.age = ageInYears(this.formModel.birthDate) || 0;

      return this.formModel.age >= MIN_LEARNER_AGE ? true : 'Age is too low';
    },

    async signUp() {
      // Mark all fields as touched to trigger validation
      this.emailTouched = true;
      this.firstNameTouched = true;
      this.lastNameTouched = true;
      this.dobTouched = true;
      this.phoneNumberTouched = true;

      this.asuoDataLayerClick('signup', '9');

      this.emailValidation();
      this.firstNameValidation();
      this.lastNameValidation();
      this.updateDOBFromDate(); // Update DOB first to populate dob object
      this.dobValidationRule(); // Then validate
      this.validatePhoneNumber(); // Validate phone number
      this.addressCheck();

      if (
        !this.emailErrorMsg &&
        !this.firstNameErrorMsg &&
        !this.lastNameErrorMsg &&
        !this.dobErrorMsg &&
        !this.fields.phoneNumber.error &&
        !this.fields.address.error &&
        !this.hasNonEnglishAddress
      ) {
        if (this.$refs.form.validate()) {
          this.$session.set('contactFormModel', this.signupFormData);
          this.$session.set('LearnerFormModel', this.signupFormData);

          // Safety net for a learner who outran the inline check.
          if (await this.offerExistingAccountMatch({ fromSubmit: true })) return;

          this.$router.push({ name: 'ConfirmRegistrationv2' });
        }
      }
    },

    // The date input emits on every keystroke, so give the learner a beat to finish typing.
    scheduleExistingAccountMatch() {
      clearTimeout(this.accountMatchTimer);

      this.accountMatchTimer = setTimeout(() => this.offerExistingAccountMatch(), 500);
    },

    /**
     * ULC-3349: as soon as last name and birthday are both in, ask whether we already hold an
     * account for this learner. Runs once per name + birthday pair; a pair the learner already
     * said "not me" to is left alone. Returns true when the modal took over.
     */
    async offerExistingAccountMatch({ fromSubmit = false } = {}) {
      clearTimeout(this.accountMatchTimer);

      const key = this.accountMatchKey;

      if (!this.accountMatchEligible || !key || this.showAccountMatch) return false;
      if (this.accountMatchDismissedKeys.includes(key)) return false;
      if (key === this.accountMatchCheckedKey && !this.accountMatches.length) return false;

      if (key !== this.accountMatchCheckedKey) {
        // Continue can land here while the inline check is still out; joining that request
        // keeps it one lookup per pair and stops the two responses racing each other.
        if (!this.accountMatchInFlight || this.accountMatchInFlight.key !== key) {
          this.accountMatchInFlight = {
            key,
            request: this.$conn.findExistingAccountMatches({
              lastName: this.formModel.lastName.trim(),
              birthDate: this.formModel.birthDate,
              asuRite: this.asuriteForMatch
            })
          };
        }

        const inFlight = this.accountMatchInFlight;

        // Inline, the lookup runs quietly behind the form; only Continue earns a spinner.
        if (fromSubmit) this.activeLoading = true;

        let matches;

        try {
          matches = await inFlight.request;
        } catch (error) {
          // A failed lookup must cost the learner nothing, and leaving the key unchecked lets
          // the next blur try again.
          this.logError('offerExistingAccountMatch', error);

          return false;
        } finally {
          if (fromSubmit) this.activeLoading = false;
          if (this.accountMatchInFlight === inFlight) this.accountMatchInFlight = null;
        }

        // The learner kept typing while we looked; the next blur/change re-runs for the new pair.
        if (key !== this.accountMatchKey) return false;

        this.accountMatches = matches;
        this.accountMatchCheckedKey = key;
      }

      if (!this.accountMatches.length) return false;

      this.accountMatchFromSubmit = fromSubmit;
      this.showAccountMatch = true;

      return true;
    },

    // "This isn't my email" / close: back to the form. If they were already on Continue, keep going.
    dismissAccountMatch() {
      this.showAccountMatch = false;

      const key = this.accountMatchKey;

      if (key && !this.accountMatchDismissedKeys.includes(key)) {
        this.accountMatchDismissedKeys.push(key);
      }

      if (this.accountMatchFromSubmit) {
        this.accountMatchFromSubmit = false;
        this.$router.push({ name: 'ConfirmRegistrationv2' });
      }
    },

    // The learner already has an account, so there is no registration left to finish. Drop the
    // half-filled form and hand them to the portal through a full load, so the session
    // initializer re-reads the now-linked learner rather than the stale pre-link state.
    goToLinkedAccount() {
      this.showAccountMatch = false;

      this.$session.remove('contactFormModel');
      this.$session.remove('LearnerFormModel');
      sessionStorage.removeItem('registrationEmail');
      useRegistrationStore().clearRegistrationData();

      window.location.assign('/dashboard');
    },

    enterkeyprevent() {
      var el = document.getElementById('enterKeyPrevent');
      if (el) {
        el.addEventListener('keypress', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
          }
        });
      }
    },

    resultLookup() {
      // Google places autocomplete logic will be added here
    },

    parseAddressComponents(addressComponents) {
      let mailingAddress = {};
      let googleCountryName = '';
      let googleStateName = '';
      let googleCityName = '';
      let googleCountryCode = '';

      addressComponents.forEach((component) => {
        const types = component.types;
        if (types.includes('street_number')) {
          mailingAddress.streetNumber = component.longText;
        }
        if (types.includes('route')) {
          mailingAddress.route = component.longText;
        }
        if (types.includes('locality')) {
          googleCityName = component.longText;
        }
        if (types.includes('administrative_area_level_1')) {
          googleStateName = component.longText;
        }
        if (types.includes('country')) {
          googleCountryName = component.longText;
          googleCountryCode = component.shortText;
        }
        if (types.includes('postal_code')) {
          this.formModel.mailingAddress.zipCode = component.longText;
        }
      });

      // Match country from Google API to our country data
      // Google returns 2-char code (e.g., "TR"), we need to find the 3-char code (e.g., "TUR")
      if (googleCountryCode || googleCountryName) {
        const matchedCountry = this.countries.find(
          (c) => c.countryCodeTwoChar === googleCountryCode || c.description === googleCountryName
        );

        if (matchedCountry) {
          this.selectedCountryCode = matchedCountry.countryCode; // Use 3-char code
          this.stateOptions = this.getStateOptions(matchedCountry.countryCode);
          // ULC-3059: keep mailingAddress.country in sync with the chosen address.
          // Previously this method only updated the visible dropdown, so foreign
          // addresses were submitted with the hardcoded 'United States' default.
          this.formModel.mailingAddress.country = matchedCountry.description;
        } else {
          this.stateOptions = [];
          // ULC-3059: fall back to Google's locale-independent ISO code, never the
          // localized display name (e.g. 中国) and never the stale default.
          this.formModel.mailingAddress.country = googleCountryCode || googleCountryName;
        }
      }

      // Match state from Google API to our state data - use state code
      // Priority: admin_area_level_1 > locality (for city-states like Istanbul)
      let matchedState = null;

      if (this.stateOptions.length > 0) {
        // First try to match administrative_area_level_1
        if (googleStateName) {
          matchedState = this.stateOptions.find(
            (s) => s.title.toLowerCase() === googleStateName.toLowerCase() || s.value === googleStateName
          );
        }

        // If no match and we have a city, check if city matches a state (e.g., Istanbul)
        if (!matchedState && googleCityName) {
          matchedState = this.stateOptions.find((s) => s.title.toLowerCase() === googleCityName.toLowerCase());
        }

        // Set state to the state code
        if (matchedState) {
          this.formModel.mailingAddress.state = matchedState.value;
        }
      } else if (googleStateName) {
        // No states in ASU API for this country — use Google's value directly
        this.formModel.mailingAddress.state = googleStateName;
      }

      // Set city - but not if it matched a state
      if (googleCityName && (!matchedState || matchedState.title.toLowerCase() !== googleCityName.toLowerCase())) {
        this.formModel.mailingAddress.city = googleCityName;
      }

      // Combine street number and route for full street address
      let street = `${mailingAddress.streetNumber || ''} ${mailingAddress.route || ''}`.trim();

      // If no street was found from components, try to extract from the formatted address
      if (!street && this.formModel.place) {
        const addressParts = this.formModel.place.split(',');
        if (addressParts.length > 0) {
          street = addressParts[0].trim();
        }
      }

      // If still no street, check addressLine2 field
      if (!street && this.formModel.mailingAddress.addressLine2) {
        street = this.formModel.mailingAddress.addressLine2;
        this.formModel.mailingAddress.addressLine2 = '';
      }

      // Ensure street is always populated
      this.formModel.mailingAddress.street = street || '';
    },

    // Placeholder; overrides the Helper mixin version
    changeTextToSentenseCase() {},

    async initializeAutocomplete() {
      try {
        await importLibrary('places');
        this.placesReady = true;
      } catch (err) {
        console.warn('[Registerv2] Failed to load Google Places API:', err);
        this.placesFailed = true;
        return;
      }

      await this.$nextTick();

      const el = this.$refs.streetField?.getAutocompleteEl();
      if (!el) return;

      el.addEventListener('gmp-select', async (event) => {
        const placePrediction = event.placePrediction;
        if (!placePrediction) return;

        try {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'displayName'] });

          this.formModel.place = foldAccents(place.formattedAddress || place.displayName?.text || '');

          if (place.addressComponents?.length) {
            this.parseAddressComponents(
              place.addressComponents.map((component) => ({
                types: component.types,
                longText: foldAccents(component.longText),
                shortText: foldAccents(component.shortText)
              }))
            );
          } else {
            this.formModel.mailingAddress.street = this.formModel.place.split(',')[0].trim();
          }
          el.value = this.formModel.mailingAddress.street || '';
          this.addressCheck();
        } catch (err) {
          console.warn('[Registerv2] fetchFields failed:', err);
        }
      });

      el.addEventListener('input', () => {
        this.formModel.place = el.value || '';
        this.formModel.mailingAddress.street = el.value || '';
        this.addressCheck();
      });
    }
  },

  created() {
    this.shouldDisplayFields = true;

    // Check if user is already logged in with connector data
    const hasLearnerData = this.$conn && this.$conn.learnerData && this.$conn.learnerData._learnerId;

    // If user is logged in AND has certificate param, redirect to dashboard to handle registration (if feature enabled)
    if (hasLearnerData && this.$route.query.certificate && isFeatureEnabled('CERTIFICATE_MANAGEMENT')) {
      sessionStorage.setItem('pendingCertificate', this.$route.query.certificate);
      this.$router.push({
        name: 'dashboard',
        query: { certificate: this.$route.query.certificate }
      });
      return;
    }

    // Capture and store query parameters in registration store
    const registrationStore = useRegistrationStore();
    if (this.$route.query && Object.keys(this.$route.query).length > 0) {
      registrationStore.setPreservedQueryParams(this.$route.query);
    }
    registrationStore.setCurrentStep('register');

    // Capture certificate parameter for post-registration handling (if feature enabled)
    if (this.$route.query.certificate && isFeatureEnabled('CERTIFICATE_MANAGEMENT')) {
      sessionStorage.setItem('pendingCertificate', this.$route.query.certificate);
    }
    // Capture pathway parameter for post-registration enrollment
    if (this.$route.query.pathway) {
      sessionStorage.setItem('pendingPathway', this.$route.query.pathway);
    }

    // Capture pathway parameter for post-registration enrollment
    if (this.$route.query.pathway) {
      sessionStorage.setItem('pendingPathway', this.$route.query.pathway);
    }

    // Capture certificate parameter for post-registration handling (if feature enabled)
    if (this.$route.query.certificate && isFeatureEnabled('CERTIFICATE_MANAGEMENT')) {
      sessionStorage.setItem('pendingCertificate', this.$route.query.certificate);
    }

    // Get session data for form persistence
    const sessionData = this.$session.get('contactFormModel') || this.$session.get('LearnerFormModel');
    if (sessionData) {
      this.formModel = { ...this.formModel, ...sessionData };
      this.contact = sessionData;

      // Restore DOB fields if they exist
      if (sessionData.birthDate) {
        this.formModel.birthDate = sessionData.birthDate;
        const birthDate = new Date(sessionData.birthDate);
        this.dob.birth_year = birthDate.getFullYear();
        this.dob.birth_month = this.months[birthDate.getMonth()];
        this.dob.birth_day = birthDate.getDate().toString();
      }
    }

    // Check if email is provided via sessionStorage, props, or route
    // This should override any session data for email
    // Priority: sessionStorage > props.email > route.params.email > route.query.email
    const emailFromSessionStorage = sessionStorage.getItem('registrationEmail');
    const emailFromRoute = this.email || this.$route.params.email || this.$route.query.email;
    const emailToUse = emailFromSessionStorage || emailFromRoute;

    if (emailToUse) {
      this.formModel.emailId = emailToUse;
      this.emailVerified = true; // Mark as verified since it came from the previous step

      // Clear the temporary storage after using it
      if (emailFromSessionStorage) {
        sessionStorage.removeItem('registrationEmail');
      }
    }

    // Set form data from route params if provided (for locked fields)
    if (this.$route.params.firstName) {
      this.formModel.firstName = this.$route.params.firstName;
    }
    if (this.$route.params.lastName) {
      this.formModel.lastName = this.$route.params.lastName;
    }
    if (this.$route.params.birthDate) {
      this.formModel.birthDate = this.$route.params.birthDate;
      const birthDate = new Date(this.$route.params.birthDate);
      this.dob.birth_year = birthDate.getFullYear();
      this.dob.birth_month = this.months[birthDate.getMonth()];
      this.dob.birth_day = birthDate.getDate().toString();
    }

    // Only use connector learnerData if no session data exists (not logged out)
    this.contactFormModel = !sessionData && this.$conn && this.$conn.learnerData ? this.$conn.learnerData : {};

    for (var i = new Date().getFullYear() - MIN_LEARNER_AGE; i >= 1900; i--) {
      this.years.push(i);
    }
  },

  async mounted() {
    await this.fetchCountryData();

    // Initialize state options for default country
    if (this.formModel.mailingAddress.country) {
      const matchedCountry = this.countryOptions.find((c) => c.title === this.formModel.mailingAddress.country);
      if (matchedCountry) {
        this.selectedCountryCode = matchedCountry.value;
        this.stateOptions = this.getStateOptions(matchedCountry.value);
      }
    }

    this.$nextTick(() => {
      const emailFromRoute = this.email || this.$route.params.email || this.$route.query.email;

      if (emailFromRoute && !this.formModel.emailId) {
        this.formModel.emailId = emailFromRoute;
        this.emailVerified = true;
      }

      this.initializeAutocomplete();
    });
  },

  beforeUnmount() {
    clearTimeout(this.accountMatchTimer);
  }
});
</script>

<style lang="stylus" scoped>
// Figma: Registration for learners under 18, Desktop - 4 (96:1341)
$gray-1 = var(--asu-grayscale-gray-1)
$gray-3 = var(--asu-grayscale-gray-3)
$gray-5 = #d0d0d0
$error = var(--system-colors-error)

.registration
  padding-top 48px
  padding-bottom 48px

.registration__column
  display flex
  flex-direction column
  gap 24px

.registration__back
  display inline-flex
  align-items center
  gap 8px
  align-self flex-start
  padding 0
  background none
  border 0
  cursor pointer
  font-size 16px
  font-weight 700
  line-height 24px
  letter-spacing -0.48px
  color $gray-1
  text-decoration underline

.registration__back-icon
  transform rotate(90deg)

.registration__title
  margin 0
  text-align center
  font-size 40px
  font-weight 700
  line-height 44px
  letter-spacing -1.2px
  color $gray-1

.registration__section-title
  margin 0
  font-size 16px
  font-weight 700
  line-height 24px
  letter-spacing -0.48px
  color $gray-1

.registration__form
  align-items start

  // FormField label → Figma "Label": Arial Bold 16/24, red asterisk 4px after
  :deep(.form-field__label)
    font-weight 700
    line-height 24px

  :deep(.form-field__required)
    margin-left 4px

  // Vuetify outlined field → Figma "Form field": 42px, 1px #d0d0d0, 2px radius, 16px inset
  :deep(.v-input)
    --v-input-control-height 42px
    --v-input-padding-top 9px
    font-size 16px

  :deep(.v-field)
    --v-field-padding-bottom 9px
    --v-field-padding-start 16px
    --v-field-padding-end 16px
    border-radius 2px
    background-color #fff
    color $gray-1

  :deep(.v-field__outline)
    --v-field-border-opacity 1
    color $gray-5

  :deep(.v-field--focused .v-field__outline)
    color $gray-1

  :deep(.v-field--error .v-field__outline)
    color $error

  :deep(.v-field__input)
    letter-spacing 0

  :deep(.v-field__input input)
    margin 0

  :deep(.v-field__input::placeholder),
  :deep(.v-field__input input::placeholder)
    color $gray-3
    opacity 1

  :deep(.v-input__details)
    padding-top 4px
    min-height 0

  :deep(.v-messages__message)
    color $error
    font-size 12px
    line-height 18px

  // Legacy main.css gives date inputs a 34px+ line-height, which stretches the field to 58px
  // display block (not Vuetify's flex) puts the calendar icon at the right edge
  :deep(.registration__date input)
    display block
    line-height 24px !important

  :deep(.registration__date input::-webkit-datetime-edit),
  :deep(.registration__date input::-webkit-datetime-edit-fields-wrapper)
    padding 0

  :deep(.registration__date .v-field:not(.v-field--dirty) input)
    color $gray-3

  :deep(.registration__date input::-webkit-calendar-picker-indicator)
    width 24px
    height 24px
    padding 0
    background url('@/assets/icons/calendar-regular-gray.svg') center / 24px no-repeat
    cursor pointer

  // Places autocomplete: its shadow parts aren't stylable, but the host's box and gmp-mat vars are
  :deep(gmp-place-autocomplete)
    height 42px
    border 1px solid $gray-5
    border-radius 2px
    font 16px / 24px Arial, sans-serif
    --gmp-mat-font-family Arial, sans-serif
    --gmp-mat-color-on-surface $gray-1
    --gmp-mat-color-on-surface-variant $gray-3

  :deep(gmp-place-autocomplete:focus-within)
    border-color $gray-1
    box-shadow inset 0 0 0 1px $gray-1

  :deep(gmp-place-autocomplete.gmp-input-error)
    border-color $error

// Stacked on mobile; from sm the M.I. column is a fixed 128px so "Middle initial" fits on one line
.registration__names
  display grid
  grid-template-columns minmax(0, 1fr)
  gap 24px
  align-items start
  @media (min-width: 600px)
    grid-template-columns minmax(0, 1fr) 128px minmax(0, 1fr)

.registration__middle-initial :deep(.form-field__label)
  white-space nowrap

// Same 24px square as the graduation modal's checkbox, flush left with an 8px gap
.registration__checkbox
  display flex
  align-items center
  gap 8px
  margin-bottom 0
  font-size 16px
  line-height 24px
  color $gray-1
  cursor pointer
  input
    appearance none
    -webkit-appearance none
    flex-shrink 0
    width 24px
    height 24px
    margin 0
    // gray-3, not Figma's gray-5, for 3:1 contrast on white
    border 1px solid $gray-3
    background #fff center / 16px no-repeat
    cursor pointer
    &:checked
      border-color $gray-1
      background-color $gray-1
      background-image url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath d='M3 8.5l3 3 7-7' fill='none' stroke='%23fff' stroke-width='2'/%3E%3C/svg%3E")
    &:focus
      outline none
    &:focus-visible
      outline 2px solid $gray-1
      outline-offset 2px
    // High contrast themes drop the custom fill, so fall back to the native box and tick
    @media (forced-colors: active)
      &, &:checked
        appearance auto
        -webkit-appearance auto
        background none
// The school name label can wrap to two lines beside School Type; keep both fields level
.registration__hs-school
  align-self end
.registration__label
  display block
  margin-bottom 8px
  font-size 16px
  font-weight 700
  line-height 24px
  color $gray-1

.registration__required
  margin-left 4px
  color $error

.registration__radios
  display flex
  flex-direction column
  gap 8px

.registration__radio
  display flex
  align-items center
  gap 8px
  margin 0
  font-size 16px
  font-weight 400
  line-height 24px
  color $gray-1
  cursor pointer

  input
    appearance none
    -webkit-appearance none
    flex-shrink 0
    width 24px
    height 24px
    margin 0
    border 1px solid $gray-5
    border-radius 50%
    background #fff
    cursor pointer

    &:checked
      background $gray-1
      box-shadow inset 0 0 0 5px #fff

    &:focus-visible
      outline 2px solid $gray-1
      outline-offset 2px

// vue-tel-input → Figma "Phone dropdown": flag segment + input, joined 42px outline
.registration__phone
  :deep(.vue-tel-input)
    height 42px
    border 1px solid $gray-5
    border-radius 2px
    box-shadow none

  :deep(.vue-tel-input:focus-within)
    border-color $gray-1
    box-shadow inset 0 0 0 1px $gray-1

  :deep(.vti__dropdown)
    flex-direction row
    align-items center
    padding 0 16px
    border-right 1px solid $gray-5

  :deep(.vti__selection)
    gap 8px
    font-size 16px

  :deep(.vti__input)
    padding 0 16px
    font-size 16px
    color $gray-1
    border-radius 0 2px 2px 0

  :deep(.vti__input::placeholder)
    color $gray-3
    opacity 1

.registration__phone--error :deep(.vue-tel-input)
  border-color $error

// Figma "Button": 282×48 gold pill; disabled is the same gold at 50%
.v-btn.registration__submit
  justify-self center
  width 282px
  max-width 100%
  height 48px
  padding 0 32px
  border-radius 100px
  background-color var(--asu-primary-gold)
  color $gray-1
  font-size 16px
  font-weight 700
  line-height 24px
  letter-spacing 0
  text-transform none

.v-btn.registration__submit--invalid
  opacity 0.5
</style>
